import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, MapPin, Clock, Star, Tag, ExternalLink, MessageSquare, Share2 } from 'lucide-react';
import MediaCarousel from './MediaCarousel';
import RequestQuoteModal from './RequestQuoteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read a translation-aware field off a partner package.
 * `translations.{lang}` overrides the source field when present and non-empty.
 */
const tField = (pkg, lang, key) => {
  const tr = (pkg?.translations || {})[lang];
  if (tr && tr[key] != null && String(tr[key]).trim()) return tr[key];
  return pkg?.[key] ?? '';
};

const PackageDetailsModal = ({ open, onClose, pkg }) => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  const [quoteOpen, setQuoteOpen] = useState(false);

  if (!open || !pkg) return null;

  // Use full media[] gallery; fall back to the cover image if empty.
  const media = (pkg.media && pkg.media.length > 0) ? pkg.media : (pkg.image ? [pkg.image] : []);

  const title = tField(pkg, lang, 'title');
  const destination = tField(pkg, lang, 'destination');
  const duration = tField(pkg, lang, 'duration');
  const description = tField(pkg, lang, 'description');
  const highlights = (() => {
    const v = tField(pkg, lang, 'highlights');
    return Array.isArray(v) ? v.filter(Boolean) : [];
  })();
  const tags = (() => {
    const v = tField(pkg, lang, 'tags');
    return Array.isArray(v) ? v.filter(Boolean) : [];
  })();

  const handleCTA = (e) => {
    e.preventDefault();
    if (pkg.booking_type === 'quote_request') {
      setQuoteOpen(true);
      return;
    }
    if (pkg.booking_type === 'external_link' && pkg.supplier_url) {
      window.open(pkg.supplier_url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Default (iframe): jump to package viewer.
    onClose();
    window.location.href = `/package-viewer?destination=${encodeURIComponent(pkg.supplier_url || '')}`;
  };

  const ctaLabel = pkg.booking_type === 'quote_request'
    ? t('quote.cta')
    : pkg.booking_type === 'external_link'
      ? 'Book Now'
      : 'View Availability';
  const CtaIcon = pkg.booking_type === 'quote_request' ? MessageSquare : ExternalLink;

  // Share button — uses /api/share/package/{id} which returns OG/Twitter HTML
  // for crawlers (iMessage, WhatsApp, X, FB, Slack) and JS-redirects humans
  // to the SPA. Falls back to copy-to-clipboard if the Web Share API is missing.
  // We use window.location.origin (NOT REACT_APP_BACKEND_URL) so a share copied
  // from chimbatravel.com produces a chimbatravel.com link, not a preview host.
  const handleShare = async () => {
    const origin = (typeof window !== 'undefined' && window.location?.origin) || BACKEND_URL;
    const shareUrl = `${origin}/api/share/package/${pkg.id}`;
    const shareData = { title: title || 'Chimba Travel', text: description || '', url: shareUrl };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // user cancelled — fall through to clipboard fallback
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied — paste it anywhere for a rich preview.');
    } catch {
      toast.error('Could not copy share link');
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="package-details-modal"
      >
        <div
          className="bg-cream rounded-sm border-2 border-maroon shadow-2xl max-w-4xl w-full my-8 max-h-[92vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 p-5 border-b border-maroon/10 sticky top-0 bg-cream z-10">
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-maroon truncate" data-testid="package-details-title">{title}</h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-maroon/70 mt-1">
                {destination && <span className="inline-flex items-center gap-1"><MapPin size={14} className="text-terracotta" /> {destination}</span>}
                {duration && <span className="inline-flex items-center gap-1"><Clock size={14} className="text-terracotta" /> {duration}</span>}
                {pkg.rating && <span className="inline-flex items-center gap-1"><Star size={14} className="text-terracotta fill-terracotta" /> {Number(pkg.rating).toFixed(1)}</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-maroon/50 hover:text-maroon p-1"
              aria-label="Close"
              data-testid="package-details-close-btn"
            >
              <X size={22} />
            </button>
          </div>

          {/* Carousel */}
          {media.length > 0 ? (
            <div className="border-b border-maroon/10" data-testid="package-details-carousel">
              <MediaCarousel media={media} alt={title} aspectClassName="aspect-[16/9]" />
            </div>
          ) : null}

          {/* Body */}
          <div className="p-5 space-y-6">
            {description ? (
              <div className="prose prose-sm max-w-none text-maroon/80 whitespace-pre-line" data-testid="package-details-description">
                {description}
              </div>
            ) : (
              <p className="text-maroon/40 italic">No detailed description has been added for this package yet.</p>
            )}

            {highlights.length > 0 && (
              <div data-testid="package-details-highlights">
                <h3 className="text-sm font-bold uppercase tracking-wide text-maroon/60 mb-2">Highlights</h3>
                <ul className="space-y-1.5 text-sm text-maroon/80">
                  {highlights.map((h, idx) => (
                    <li key={idx} className="flex gap-2">
                      <span className="text-terracotta mt-0.5">•</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="package-details-tags">
                {tags.map((tg, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-sand/50 text-maroon/70 text-xs rounded-sm">
                    <Tag size={11} /> {tg}
                  </span>
                ))}
              </div>
            )}

            {/* Pricing + CTA bar */}
            <div className="flex items-end justify-between gap-4 flex-wrap pt-4 border-t border-maroon/10">
              <div>
                <p className="text-xs text-maroon/50 uppercase tracking-wider">Starting at</p>
                <p className="text-3xl font-heading font-bold text-maroon">{pkg.price || '—'}</p>
                {pkg.supplier && <p className="text-xs text-maroon/50 mt-1">Operated by {pkg.supplier}</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 border border-maroon/30 text-maroon hover:bg-maroon/5 font-semibold uppercase tracking-wide rounded-sm transition-all px-4 py-3 text-sm"
                  data-testid="package-details-share-btn"
                  title="Share with rich preview"
                >
                  <Share2 size={14} />
                  Share
                </button>
                <button
                  type="button"
                  onClick={handleCTA}
                  className="inline-flex items-center gap-2 bg-terracotta text-white hover:bg-terracotta/90 font-bold uppercase tracking-wide rounded-sm transition-all hover:translate-y-[-2px] px-6 py-3 text-sm"
                  data-testid="package-details-cta-btn"
                >
                  <CtaIcon size={14} />
                  {ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nested quote modal — sits ABOVE the details modal */}
      <RequestQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} pkg={pkg} />
    </>
  );
};

export default PackageDetailsModal;
